import TextField from '@mui/material/TextField';
import selectPrivacyModeEnabled from 'app-components/hooks/privacyMode/selectPrivacyModeEnabled';
import { useAppSelector } from 'hooks/redux';
import { makeStyles } from 'tss-react/mui';
import React, { useState, useEffect } from 'react';

interface CurrencyTextFieldProps {
  id: string;
  label?: string;
  placeholder?: string;
  value?: number;
  onChange: (value?: number) => void;
  error?: boolean;
  helperText?: string;
  className?: string;
  sx?: React.CSSProperties;
  showGreen?: boolean;
}

const useStyles = makeStyles()(() => ({
  privacyMask: {
    WebkitTextSecurity: 'disc',
  },
}));

const CurrencyTextField: React.FC<CurrencyTextFieldProps> = ({
  id,
  label,
  placeholder,
  value,
  onChange,
  error = false,
  helperText,
  className,
  sx,
  showGreen = false,
}) => {
  const [internalValue, setInternalValue] = useState<string>(() => {
    if (value === null || value === undefined) return '';
    return showGreen ? `+${value}` : String(value);
  });

  const privacyModeEnabled = useAppSelector(selectPrivacyModeEnabled);

  const { classes } = useStyles();

  useEffect(() => {
    if (value === null || value === undefined) {
      setInternalValue('');
    } else {
      setInternalValue(showGreen ? `+${value}` : String(value));
    }
  }, [value, showGreen]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;

    // Allow "+" prefix when showGreen, and valid USD currency values
    const currencyRegex = showGreen ? /^\+?\d{0,10}(\.\d{0,2})?$/ : /^\d{0,10}(\.\d{0,2})?$/;

    if (inputValue === '' || currencyRegex.test(inputValue)) {
      setInternalValue(inputValue);
      const numericValue = inputValue.replace(/^\+/, '');
      onChange(numericValue === '' ? undefined : parseFloat(numericValue));
    }
  };

  // non-standard attribute, we should not always rely on it
  const supportsWebkitTextSecurity =
    typeof window !== 'undefined' && window.CSS?.supports?.('-webkit-text-security', 'disc');

  const privacyModeProps =
    privacyModeEnabled && !supportsWebkitTextSecurity
      ? {
          type: 'password',
          autoComplete: 'one-time-code',
          'data-1p-ignore': 'true',
          'data-bwignore': 'true',
          'data-lpignore': 'true',
        }
      : {
          type: 'text',
        };

  const inputProps =
    privacyModeEnabled && supportsWebkitTextSecurity
      ? {
          inputProps: {
            className: classes.privacyMask,
          },
        }
      : {};

  return (
    <TextField
      id={id}
      fullWidth
      label={label}
      placeholder={placeholder || (showGreen ? '$ +0.00' : '$ 0.00')}
      value={internalValue}
      onChange={handleInputChange}
      error={error}
      helperText={error ? helperText || 'Required' : undefined}
      className={className}
      inputProps={{ inputMode: 'decimal', pattern: '\\d*' }}
      variant='outlined'
      {...privacyModeProps}
      sx={{
        '& .MuiInputBase-root': {
          backgroundColor: (theme) => theme.palette.background.paper,
          borderRadius: (theme) => theme.shape.borderRadius,
          ...(showGreen && {
            color: (theme) => theme.palette.amount.positive,
            '& input': {
              color: (theme) => theme.palette.amount.positive,
            },
          }),
        },
        '& .MuiInputBase-adornedStart': {
          alignItems: 'baseline',
          gap: 0.5,
          ...(showGreen && {
            color: (theme) => theme.palette.amount.positive,
          }),
        },
        ...sx,
      }}
      InputProps={{
        startAdornment: internalValue ? <span aria-hidden>$ </span> : undefined,
        ...inputProps,
      }}
    />
  );
};

export default CurrencyTextField;
